/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CardSize } from '../../../../types';
import type { CommonCardProps } from '../types';

import { messages } from './messages';

const legacyContainer = css({
	boxSizing: 'border-box',
	overflow: 'hidden',
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
});

const styles = cssMap({
	container: {
		height: '100%',
	},
	imageBase: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	imageFlexibleHeight: {
		minHeight: '0px',
		flex: '1 1 0',
	},
	imageFixedHeightMedium: {
		height: '140px',
	},
	imageFixedHeightLarge: {
		height: '200px',
	},
	subtitleBase: {
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	subtitleBlock: {
		height: '48px',
	},
	imageInline: {
		minWidth: '28%',
		height: '100%',
	},
	subtitleInline: {
		height: '100%',
	},
});

type ZeroResultsCardProps = { cardSize: CardSize } & Pick<CommonCardProps, 'cardDimensions'>;

const SmallCard = () => {
	return (
		<Inline testId="zero-results-card-small" xcss={styles.container}>
			<Box xcss={cx(styles.imageBase, styles.imageInline)} />
			<Inline alignBlock="center" xcss={cx(styles.subtitleBase, styles.subtitleInline)}>
				<FormattedMessage {...messages.zeroResultsTitle} />
			</Inline>
		</Inline>
	);
};

const MediumLargeCard = ({
	isImageFixedHeight = false,
	cardSize = CardSize.MEDIUM,
}: {
	cardSize?: CardSize;
	isImageFixedHeight?: boolean;
}) => {
	return (
		<Stack testId="zero-results-card" space="space.025" xcss={styles.container}>
			<Box
				xcss={cx(
					styles.imageBase,
					isImageFixedHeight
						? cardSize === CardSize.LARGE
							? styles.imageFixedHeightLarge
							: styles.imageFixedHeightMedium
						: styles.imageFlexibleHeight,
				)}
			/>
			<Inline
				alignBlock="center"
				alignInline="center"
				xcss={cx(styles.subtitleBase, styles.subtitleBlock)}
			>
				<FormattedMessage {...messages.zeroResultsTitle} />
			</Inline>
		</Stack>
	);
};

export const ZeroResultsCard = ({ cardSize, cardDimensions }: ZeroResultsCardProps) => {
	const { height } = cardDimensions;

	return (
		<div
			css={legacyContainer}
			style={{
				height,
			}}
		>
			{cardSize === CardSize.SMALL ? <SmallCard /> : <MediumLargeCard />}
		</div>
	);
};
